import React from 'react';
import useMetaLayoutContext from 'Components/UI/Meta/MetaForm/Components/MetaLayoutContext';
import { useModalContext } from 'Views/Page/Contexts/ModalContext';
import { useSnackbarContext } from 'Context/SnackbarContext';
import { usePageContentContext } from 'Views/Page/Contexts/PageContentContext';
import WizardStepDeleteButton
  from 'Views/Page/Content/Templates/WizardTemplates/WizardStepTemplate/Content/WizardStepDeleteButton';
import { FDPrimaryActionButton, FDStack } from '@facet-wealth/design';
import { dataAttrs } from 'Helpers/Testing';
import MetaLayoutRemainingFields from 'Components/UI/Meta/MetaForm/Components/MetaLayout/MetaLayoutRemainingFields';
import MetaControlField from 'Components/UI/Meta/MetaForm/Components/MetaControlField';
import { AttachmentViewer } from 'Organisms/AttachmentViewer';
import { DialogContent, DialogTitle } from 'Views/Page/Content/Modal/Components';

const FacetPlanAddIncome = () => {
  const { saving } = useMetaLayoutContext();
  const { modalCollection, clearModalCollection } = useModalContext();
  const { displayErrorMessage, displaySuccess } = useSnackbarContext();
  const { refreshPageContentCollection } = usePageContentContext();
  const [loading, setLoading] = React.useState(false);

  const resource = modalCollection.resources().head();
  const isEditing = Boolean(resource.resourceId !== undefined);
  const canSubmit = resource.meta().canSubmit();
  const canDelete = resource.meta().canDelete();

  const handleOnSubmit = async () => {
    try {
      setLoading(true);
      await resource.submit();

      // Close modal / display Success!
      await clearModalCollection();
      displaySuccess(`Successfully ${isEditing ? 'Modified' : 'Added'} Income`);

      // Refresh
      await refreshPageContentCollection();
    } catch (err) {
      displayErrorMessage(err, err.errors);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      if (isEditing && canDelete) {
        await resource.delete();

        // Close modal / display Success!
        await clearModalCollection();
        displaySuccess('Successfully Removed Income!');
      }
    } catch (err) {
      displayErrorMessage(err, err.errors);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <DialogTitle value={`${isEditing ? 'Modify' : 'Add'} Income`} />
      <DialogContent>
        <FDStack spacing={4}>
          <FDStack direction={'column'} spacing={2} flexShrink={0} minWidth={400}>
            <MetaControlField attr="description" />
            <MetaControlField attr="frequencyGrossValue" />
            <MetaControlField attr="annualGrossValue" />
            <MetaControlField attr="amountIsPerFrequency" />
            <MetaControlField attr="isTaxFree" />
            <MetaControlField attr="affectedByInflation" />
            <MetaControlField attr="frequency" />
            <MetaControlField attr="incomeCategoryId" />
            <MetaLayoutRemainingFields noAccordion />
            <MetaControlField attr="startType" />
            <MetaControlField attr="startDate" />
            <MetaControlField attr="startAtAge" />
            <MetaControlField attr="endType" />
            <MetaControlField attr="endDate" />
            <MetaControlField attr="endAtAge" />
            {isEditing && canDelete && (
            <WizardStepDeleteButton
              onDelete={handleDelete}
              onDeleteSuccess={refreshPageContentCollection}
              dialogProps={{
                title: 'Are you sure?',
                message: 'Removing this item will delete all of its information. This cannot be undone.',
                cancelLabel: 'Nevermind',
                confirmLabel: 'Remove',
              }}
            />)}
            {canSubmit && (
            <FDPrimaryActionButton
              onClick={handleOnSubmit}
              label="Submit"
              loading={saving || loading}
              {...dataAttrs('facet-plan-add-income-button', resource)}
            />
            )}
          </FDStack>
          <AttachmentViewer />
        </FDStack>
      </DialogContent>
    </>
  );
};

FacetPlanAddIncome.defaultProps = {};
FacetPlanAddIncome.propTypes = {};

export default FacetPlanAddIncome;
